import { z } from "zod";
import { DraftReturn } from "../../draft-return/draft-return";
import { ReturnableItem } from "../../draft-return/draft-return-items";
import { getReturnableItemFlow } from "../../draft-return/util";
import {
  InputPartType,
  ReturnFlow,
  returnFlowJsonFormat,
  Step,
} from "../../return-flow";

export const AiReturnReasonSchema = z.object({
  prompt: z.string(),
  answer: z.string(),
  additionalAiInfo: z.string().nullish(),
});

export type AiReturnReason = z.infer<typeof AiReturnReasonSchema>;

/**
 * Used to turn return reasons into text that will be able to be consumed effectively by an LLM.
 *
 * @returns Ordered list of questions (message) and responses (value)
 */
export function returnReasonsResponsesToText(
  multipleChoiceAnswers_: readonly { step: number; answer: number }[],
  inputAnswers_: readonly {
    message: string;
    value: string | string[] | URL[];
    type: InputPartType;
    step?: number | undefined | null;
  }[],
  returnFlow: ReturnFlow,
): AiReturnReason[] {
  // Map multiple choice answers to their questions and response values.
  const multipleChoiceAnswers = multipleChoiceAnswers_
    .map((choice) => {
      const stepObject = returnFlow.steps[choice.step] as Step.MultipleChoice;
      const choiceOption = stepObject.options[choice.answer];
      if (choiceOption && choiceOption.name) {
        let additionalAiInfo = "";
        if (stepObject.additionalAiInfo) {
          additionalAiInfo += `${stepObject.additionalAiInfo}`;
        }
        if (choiceOption.additionalAiInfo) {
          additionalAiInfo += `${additionalAiInfo ? "\n" : ""}${choiceOption.additionalAiInfo}`;
        }

        return {
          type: "multiple_choice",
          prompt: stepObject.message,
          additionalAiInfo: additionalAiInfo || undefined,
          answer: choiceOption.name,
          step: choice.step,
        };
      }
      return undefined;
    })
    .filter((answer) => answer !== undefined);

  // Filter inputs down to text answers
  const inputAnswers = inputAnswers_
    .map((answer) => {
      if (
        answer.type === InputPartType.TEXTAREA &&
        typeof answer.value === "string"
      ) {
        // We don't want URLs here, this is supposed to be consumable by an LLM.
        return {
          type: "text_area",
          prompt: answer.message,
          answer: answer.value,
          // Additional ai info is currently only supported for multiple choice answers.
          additionalAiInfo: undefined,
          step: answer.step ?? 0,
        };
      }
      return undefined;
    })
    .filter((answer) => answer !== undefined);

  return multipleChoiceAnswers
    .concat(inputAnswers)
    .sort((a, b) => a.step - b.step)
    .map((response) => ({
      prompt: response.prompt,
      answer: response.answer,
      additionalAiInfo: response.additionalAiInfo,
    }));
}

export function getReturnFlow(
  returnItem: ReturnableItem | undefined,
  draftReturn: DraftReturn | undefined,
): ReturnFlow {
  return returnFlowJsonFormat.read(
    getReturnableItemFlow(returnItem, draftReturn),
  );
}
